/**
 * Why masking exists: every ESP ships proprietary tags its own renderer
 * resolves, and ours has never heard of them. `{% abort_message("…") %}` is a
 * real tag to Braze and `tag "abort_message" not found` to liquidjs, so one of
 * them anywhere in a template fails the parse of *everything else* — the
 * template lints as broken and the export is refused, over syntax that is
 * perfectly valid for its destination.
 *
 * So we swap those tags for inert placeholder text, parse and render the
 * template around them, then put the customer's own bytes back verbatim. The
 * tags are never interpreted, only stepped over.
 *
 * Which names get masked is per dialect and deliberately short — see
 * `braze-tag-mask.ts` and `django-tag-mask.ts`. Masking a name the vendor does
 * not document would hide a genuine typo (`{% connceted_content %}`) behind a
 * successful render.
 */

import { MAX_TAG_LENGTH } from "./tag-bounds";

/**
 * Reached from the export preflight with request-supplied HTML, so the scan for
 * `%}` is bounded — see MAX_TAG_LENGTH. Unlike the spans in braze-template-refs,
 * this pattern has a capture group, so V8's required-substring prefilter does not
 * rescue it from an unterminated `{%`.
 */
export const PROVIDER_TAG_PATTERN = new RegExp(
  String.raw`\{%-?\s*([A-Za-z_][A-Za-z0-9_]*)\b(?=[\s\S]{0,${MAX_TAG_LENGTH}}?-?%\})[\s\S]*?-?%\}`,
  "g",
);

export interface MaskedProviderTags {
  maskedTemplate: string;
  maskedTags: string[];
  placeholders: ReadonlyMap<string, string>;
}

/**
 * A colon-namespaced output tag like `{{ses:openTracker}}` — Amazon SES's
 * open-tracking pixel marker. A colon is invalid in both Liquid and Handlebars
 * identifier grammars, so `ns:name` inside bare output braces can only be a
 * vendor-resolved token, never a user variable — which is what makes masking
 * the whole namespace safe where `{% %}` masking must stick to documented
 * names.
 */
export const COLON_NAMESPACED_OUTPUT_TAG_PATTERN =
  /\{\{\s*[A-Za-z_][A-Za-z0-9_]*:[A-Za-z_][A-Za-z0-9_.]*\s*\}\}/g;

export function maskColonNamespacedOutputTags(
  template: string,
  placeholderPrefix = "__HT_VENDOR_TAG__",
): MaskedProviderTags {
  const prefix = availablePrefix(template, placeholderPrefix);
  const maskedTags: string[] = [];
  const placeholders = new Map<string, string>();
  const maskedTemplate = template.replace(
    COLON_NAMESPACED_OUTPUT_TAG_PATTERN,
    (tag: string) => {
      const placeholder = `${prefix}${maskedTags.length}__`;
      maskedTags.push(tag);
      placeholders.set(placeholder, tag);
      return placeholder;
    },
  );

  return { maskedTemplate, maskedTags, placeholders };
}

/** Every placeholder starts with this, so a prefix absent from the source text
 * is enough to keep placeholder-like source from being restored as a tag. */
function availablePrefix(template: string, prefix: string): string {
  let candidate = prefix;
  let suffix = 0;
  while (template.includes(candidate)) {
    candidate = `${prefix}${++suffix}_`;
  }
  return candidate;
}

/**
 * Replaces every `{% name … %}` whose name is in `tagNames` with inert
 * placeholder text, so a tag the ESP resolves itself does not fail the parse of
 * everything around it. Restorable byte-for-byte, which is what lets the render
 * path put the customer's own tag back after a successful render.
 */
export function maskProviderTags({
  template,
  tagNames,
  placeholderPrefix,
}: {
  template: string;
  tagNames: ReadonlySet<string>;
  placeholderPrefix: string;
}): MaskedProviderTags {
  const prefix = availablePrefix(template, placeholderPrefix);
  const maskedTags: string[] = [];
  const placeholders = new Map<string, string>();
  const maskedTemplate = template.replace(
    PROVIDER_TAG_PATTERN,
    (tag: string, name: string) => {
      if (!tagNames.has(name)) {
        return tag;
      }
      const placeholder = `${prefix}${maskedTags.length}__`;
      maskedTags.push(tag);
      placeholders.set(placeholder, tag);
      return placeholder;
    },
  );

  return { maskedTemplate, maskedTags, placeholders };
}

export function restoreProviderTags(
  template: string,
  placeholders: ReadonlyMap<string, string>,
): string {
  let restored = template;
  for (const [placeholder, tag] of placeholders) {
    restored = restored.split(placeholder).join(tag);
  }
  return restored;
}
